import { useRef, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { AgGridReact } from 'ag-grid-react';
import { Flex, Text } from '@chakra-ui/react';
import { isDate, isValid } from 'date-fns';

import colors from '../../theme/colors';

import { dateValueFormatter, validateFields, createDateAsUTC, lenientParse } from '../../services/items';

import SortAscIcon from '../../icons/sort-asc.svg';
import SortDescIcon from '../../icons/sort-desc.svg';
import SortNoneIcon from '../../icons/sort-none.svg';
import FirstPageIcon from '../../icons/first-page.svg';
import LeftArrowIcon from '../../icons/prev-page.svg';
import LastPageIcon from '../../icons/last-page.svg';
import RightArrowIcon from '../../icons/next-page.svg';

import DataGridButton from '../hedges/DataGridButton';
import StudyTrackerStatusBadge from '../studies/StudyTrackerStatusBadge';
import StudyTrackerName from '../studies/StudyTrackerName';

import DataGridCalendar from './DataGridCalendar';
import DataGridSelect from './DataGridSelect';
import DataGridCheckbox from './DataGridCheckbox';
import DataGridCheckboxEditor from './DataGridCheckboxEditor';
import DataGridProgressBar from './DataGridProgressBar';
import DataGridNoRowsOverlay from './DataGridNoRowsOverlay';

const cellValidationThreshold = 10000;

const DataGrid = ({
    columns,
    className,
    isInitialRequestSent,
    onDelete,
    noDataMessageId,
    onDataChange,
    rowHeight = 44,
    pagination = true,
    ...rest
}) => {
    const intl = useIntl();

    // useState is not suitable as we need something that is updated synchronous
    const isPasting = useRef(false);
    const rowsChanged = useRef([]);

    const columnTypes = {
        date: {
            valueFormatter(params) {
                if (params.value == null || params.value === '') {
                    return '';
                }

                const def = params.column.getColDef();
                const dateFormat = def.cellEditorParams.dateFormat;

                return dateValueFormatter(params, dateFormat);
            },
            cellEditor: 'DataGridCalendar',
            minWidth: 250,
            valueSetter(params) {
                const def = params.column.getColDef();
                const value = params.newValue;

                // commit value as null if empty or invalid
                if (value == null || value === '' || value === 'Invalid Date') {
                    if (def.cellEditorParams?.nullable) {
                        params.data[def.field] = null;

                        return true;
                    }

                    return false;
                }

                // commit value if it's instance of Date or a valid string like date except Numbers
                if (isDate(value) || (isValid(new Date(value)) && Number.isNaN(Number(value)))) {
                    params.data[def.field] = createDateAsUTC(lenientParse(value)).toISOString();

                    return true;
                }

                return false;
            },
            cellEditorParams: {
                dateFormat: undefined,
            },
        },
        number: {
            valueFormatter(params) {
                if (params.value == null || params.value === '') {
                    return '';
                }
                const def = params.column.getColDef();
                if (def.cellEditorParams.format === 'percent') {
                    return `${params.value}%`;
                }
                if (def.cellEditorParams.format === 'currency') {
                    return `$${params.value}`;
                }

                return params.value;
            },
            valueSetter(params) {
                const def = params.column.getColDef();
                const value = parseFloat(params.newValue);

                if (Number.isNaN(value)) {
                    params.data[def.field] = def.cellEditorParams.required ? 0 : null;
                } else {
                    if (
                        (def.cellEditorParams.min === undefined || def.cellEditorParams.min <= value) &&
                        (def.cellEditorParams.max === undefined || def.cellEditorParams.max >= value)
                    ) {
                        params.data[def.field] = value;
                    }
                }

                return true;
            },
            cellEditorParams: {
                required: false,
            },
        },
        checkbox: {
            cellRenderer: 'DataGridCheckbox',
            cellEditor: 'DataGridCheckboxEditor',
            valueSetter(params) {
                const def = params.column.getColDef();
                const allowedValues = ['1', '0', 1, 0, 'true', 'false', true, false];
                const value = params.newValue;

                if (allowedValues.includes(value)) {
                    if (value === '1' || value === 1 || value === 'true' || value === true) {
                        params.data[def.field] = true;
                    }

                    if (value === '0' || value === 0 || value === 'false' || value === false) {
                        params.data[def.field] = false;
                    }

                    return true;
                }

                return false;
            },
        },
        select: {
            cellEditor: 'DataGridSelect',
            valueFormatter(params) {
                if (params.value == null || params.value === '') {
                    return '';
                }

                const def = params.column.getColDef();
                const option = def.cellEditorParams.options.find(({ id }) => id === params.data[def.field]);

                return option ? option.description : '';
            },
            valueSetter(params) {
                const def = params.column.getColDef();

                if (params.newValue === '') {
                    if (!def.cellEditorParams.required) {
                        // set the new dropdown value to the empty <option> if it's a non-required dropdown
                        params.data[def.field] = null;
                    }

                    // commit the new value only if it's a non-required dropdown
                    return !def.cellEditorParams.required;
                }

                const maybeId = Number(params.newValue);
                const maybeDescription = params.newValue;

                // try to find a matching select option that either matches the id property or the description
                const option = def.cellEditorParams.options.find(({ id, description }) => {
                    return id === maybeId || description === maybeDescription;
                });

                if (option) {
                    params.data[def.field] = option.id;
                }

                return !!option;
            },
            cellEditorParams: {
                options: [],
                required: true,
            },
        },
        button: {
            editable: false,
            cellRenderer: 'DataGridButton',
        },
        progressBar: {
            cellRenderer: 'DataGridProgressBar',
        },
    };

    const onDeleteData = (params) => {
        if (params.event.key === 'Delete' && !params.editing && typeof onDelete !== 'undefined') {
            // suppressMultiRangeSelection is used so there is only one range
            const [range] = params.api.getCellRanges();
            const columnLength = params.columnApi.getAllDisplayedColumns().length;

            if (range.columns.length === columnLength) {
                const start = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
                const end = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);

                const ids = [];
                for (let i = start; i <= end; i++) {
                    const node = params.api.getDisplayedRowAtIndex(i);

                    if (node) {
                        ids.push(node.id);
                    }
                }

                if (ids.length > 0) {
                    onDelete(ids);
                }
            }

            return true;
        }

        return false;
    };

    const onCopyFromGrid = (params) => {
        const def = params.column.getColDef();

        if (def.type === 'date') {
            return dateValueFormatter({ value: params.value });
        }

        if (def.type === 'checkbox') {
            return params.value ? '1' : '0';
        }

        if (def.type === 'select') {
            if (params.value == null || params.value === '') {
                return '';
            }

            const option = def.cellEditorParams.options.find(({ id }) => id === params.value);

            return option ? option.description : '';
        }

        return params.value;
    };

    const onPasteStart = (event) => {
        if (rest.onPasteStart) {
            rest.onPasteStart(event);
        }

        isPasting.current = true;
    };

    const onCellValueChanged = (event) => {
        if (rest.onCellValueChanged) {
            rest.onCellValueChanged(event);
        }

        if (!event.node) return;

        if (!rowsChanged.current.includes(event.node.id)) {
            rowsChanged.current.push(event.node.id);
        }

        if (!isPasting.current) {
            if (event.oldValue === event.newValue) return;
            propagateChanges();
        }
    };

    const onPasteEnd = (event) => {
        if (rest.onPasteEnd) {
            rest.onPasteEnd(event);
        }

        propagateChanges();

        isPasting.current = false;
    };

    const propagateChanges = () => {
        onDataChange(rowsChanged.current);
        rowsChanged.current = [];
    };

    const cellColorValidation = (params) => {
        if (cellValidationThreshold < params.api.getDisplayedRowCount()) return;

        const validate = validateFields([
            {
                ...rest.columnSchema[params.colDef.field],
                value: params.value,
            },
        ]);

        if (validate[0]) {
            return { borderColor: colors.red['900'] };
        } else {
            return { borderColor: 'none' };
        }
    };

    const noRowsOverlayComponentFramework = useMemo(() => {
        return DataGridNoRowsOverlay;
    }, []);

    return (
        <>
            {isInitialRequestSent ? (
                <AgGridReact
                    localeText={{
                        loadingOoo: intl.formatMessage({ id: 'common_loading' }),
                        noRowsToShow: intl.formatMessage({ id: 'common_no_data_found' }),
                    }}
                    defaultColDef={{
                        editable: true,
                        flex: 1,
                        minWidth: 100,
                        lockPosition: true,
                        suppressMenu: true,
                        unSortIcon: true,
                        resizable: true,
                        suppressKeyboardEvent: rest.rowModelType === 'serverSide' && onDeleteData,
                        cellStyle: rest.rowModelType === 'clientSide' && cellColorValidation,
                        // the default text editor of ag grid changes the newValue to undefined
                        // when a user enters and exits edit mode without changing anything to an empty cell
                        valueParser: (params) => {
                            return params.newValue === undefined && params.oldValue === null ? null : params.newValue;
                        },
                    }}
                    processCellForClipboard={onCopyFromGrid}
                    columnTypes={columnTypes}
                    pagination={pagination}
                    enableRangeSelection={true}
                    frameworkComponents={{
                        DataGridCalendar,
                        DataGridCheckbox,
                        DataGridCheckboxEditor,
                        DataGridProgressBar,
                        DataGridSelect,
                        DataGridButton,
                        StudyTrackerStatusBadge,
                        StudyTrackerName,
                        DataGridNoRowsOverlay,
                    }}
                    icons={{
                        sortAscending: `<img src="${SortAscIcon}" />`,
                        sortDescending: `<img src="${SortDescIcon}" />`,
                        sortUnSort: `<img src="${SortNoneIcon}" />`,
                        first: `<img src="${FirstPageIcon}" />`,
                        previous: `<img src="${LeftArrowIcon}" />`,
                        next: `<img src="${RightArrowIcon}" />`,
                        last: `<img src="${LastPageIcon}" />`,
                    }}
                    rowHeight={rowHeight}
                    suppressContextMenu={true}
                    columnDefs={columns}
                    rowData={rest.rowData}
                    onPasteStart={onPasteStart}
                    onCellValueChanged={onCellValueChanged}
                    onPasteEnd={onPasteEnd}
                    noRowsOverlayComponentFramework={noRowsOverlayComponentFramework}
                    {...rest}
                />
            ) : (
                <Flex h="300px" justify="center" align="top">
                    <Text size="sm" variant="regular">
                        {noDataMessageId && <FormattedMessage id={noDataMessageId} />}
                    </Text>
                </Flex>
            )}
        </>
    );
};

export default DataGrid;
